import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getBackend } from '../backend';
import Sheet from '../common/Sheet';
import { pickStreamUrls, listConfigCameras, cameraSection, cameraIpFromSection } from './configParser';
import { loadDiscoverDraft, saveDiscoverDraft } from './discoverDraft';
import SheetSelect from './SheetSelect';
import { askConfirm } from './confirmService';

/**
 * DiscoverCamerasSheet — cloud "Discover Cameras" (subtask #86bbm53r2).
 *
 * The ONLY "add a camera" path in cloud setup. A Dragon device runs an ONVIF scan of its LAN
 * (cloudapi → backend /ctrl); this sheet reads the cache, lets an admin re-scan, pick cameras via
 * CHECKBOXES (it's a select-to-add list, not a settings toggle), name them, and STAGES each as a
 * new [camN] section into the working device config via `onStage`. Staged cameras ride the existing
 * "Push N Changes to Device" batch (one conf_rev bump → conf_sync) — nothing is sent from here.
 *
 * Expanding a camera shows its name + stream editor. Discovery intentionally omits snapshots so a
 * list cannot grow by about 90 KB per camera and slow the setup flow.
 *
 * Data-fetch discipline (dview/lite CLAUDE.md): the cache read + rescan poll are guarded by a
 * generation token (`genRef`) so a slow response from a prior device/scan can't clobber a newer one;
 * the loop cancels on unmount/close.
 *
 * Props: deviceId (sDeviceID), config (working device config, to flag already-configured cameras),
 * token (session JWT), onStage(sel[]), onClose().
 */

// The bare host of a discovered ip ("10.0.0.5:80" → "10.0.0.5"), IPv6-aware so it normalizes to the same
// bare host cameraIpFromSection() derives from a config rec_url ("[::1]:554" → "::1", bare "::1" → "::1") —
// otherwise the assigned/added de-dup fails open for IPv6 cameras and one can be staged twice.
const hostOf = (ip) => {
  const s = String(ip || '').trim();
  if (s[0] === '[') return s.slice(1, s.indexOf(']'));       // [ipv6]:port
  const parts = s.split(':');
  return parts.length > 2 ? s : (parts[0] || '');            // >2 colons ⇒ bare ipv6 (keep whole); else strip :port
};

// Friendly default name — "AXIS M3007", not "AXIS AXIS M3007" (dedupe make when model repeats it).
const defaultName = (cam) => {
  const make = String(cam.manufacturer || '').split(' ')[0];
  const model = String(cam.model || '').trim();
  if (!make) return model || cam.ip || 'Camera';
  if (model.toLowerCase().indexOf(make.toLowerCase()) === 0) return model;
  return `${make} ${model}`.trim() || String(cam.ip || 'Camera');
};

const streams = (cam) => (Array.isArray(cam && cam.rtsp) ? cam.rtsp.filter((s) => s && s.url) : []);
const px = (s) => (Number(s.width) || 0) * (Number(s.height) || 0);
const bestStream = (cam) => {
  const s = streams(cam);
  return s.length ? [...s].sort((a, b) => px(b) - px(a))[0] : null;
};
const streamLabel = (st) =>
  st ? [st.codec, st.width && st.height ? `${st.width}×${st.height}` : null, st.fps ? `${st.fps}fps` : null].filter(Boolean).join(' · ') : '';

// Default stream pairing for a newly-ticked camera — delegates to the shared configParser.pickStreamUrls
// (ONE source of truth: rec = highest-res, live = lowest-res, distinct streams on a pixel tie), keeping
// just the two URLs. URLs (not array indices) are stored as the pick, so a choice survives a rescan that
// reorders the profile list — the URL is a stable identity; an index would silently point at the wrong
// profile after a reorder. Each picker can still override its own choice.
const defaultUrls = (cam) => { const { recUrl, liveUrl } = pickStreamUrls(cam); return { recUrl, liveUrl }; };

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const SCAN_MAX_MS = 150000; // give a device scan ~2.5 min before giving up the poll
const POLL_MS = 4000;

// The set of camera LAN hosts present in a config (by their RTSP URL host).
const hostsOf = (config) => {
  const set = new Set();
  listConfigCameras(config).forEach((n) => {
    const ip = cameraIpFromSection(cameraSection(config, n));
    if (ip) set.add(ip);
  });
  return set;
};

const DiscoverCamerasSheet = ({ deviceId, config, pristineConfig, token, replaceTarget, onStage, onClose }) => {
  const backend = getBackend();
  const replace = replaceTarget != null;   // repointing an existing camera's stream (single-select) vs adding new cameras
  const draftScope = replace ? `replace:${replaceTarget}` : 'add';
  const [phase, setPhase] = useState('loading'); // loading | ready | scanning
  const [status, setStatus] = useState('ok');    // ok | offline | never_scanned | error
  const [cameras, setCameras] = useState([]);
  const [timestamp, setTimestamp] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [selected, setSelected] = useState({});  // { [ip]: { name, recUrl, liveUrl } } — CHECKBOX = in the batch
  const [expanded, setExpanded] = useState({});   // { [ip]: true } — row EXPANDED to view/edit. INDEPENDENT of
  // selection: expanding a row to look at its streams does NOT select it; only the checkbox does. (David 2026-08-27)
  const [busyAdd, setBusyAdd] = useState(false);
  const [staleNote, setStaleNote] = useState('');   // "N picks removed/skipped — no longer available" (stale-cache protection)
  const genRef = useRef(0);
  const scanAbortRef = useRef(null);
  const mountedRef = useRef(true);

  // Hosts already ASSIGNED to the device (in the SERVER config = pristine) vs added THIS batch (staged
  // into the working config but not yet pushed) — so the find list designates each row "Assigned" vs "Added".
  // CAVEAT: host-match only sees cameras configured DIRECT-TO-CAMERA (rec_url host == the camera IP). A
  // camera fed through an aggregating NVR (rec_url = rtsp://<nvr>:<port>/…) carries the NVR's IP, not the
  // camera's, so its real IP is absent from the config and it can't be matched here — see
  // cloud-discover-feature/DRAGON-DISCOVER-FINDINGS.md #1 (device-side MAC tagging is the bulletproof fix).
  const assignedHosts = useMemo(() => hostsOf(pristineConfig), [pristineConfig]);
  const currentHosts = useMemo(() => hostsOf(config), [config]);

  // Deduped display names: two cameras of the same model (e.g. two "Zavio D5113") get their IP's last
  // octet appended so they're distinguishable in the list AND as the prefilled name.
  const dedupedNames = useMemo(() => {
    const counts = {};
    cameras.forEach((c) => { const n = defaultName(c); counts[n] = (counts[n] || 0) + 1; });
    const map = {};
    cameras.forEach((c) => {
      const base = defaultName(c);
      map[c.ip] = counts[base] > 1 ? `${base} (${hostOf(c.ip).split('.').pop()})` : base;
    });
    return map;
  }, [cameras]);
  const dispName = (cam) => dedupedNames[cam.ip] || defaultName(cam);

  const applyResult = useCallback((d) => {
    if (!d) { setPhase('ready'); return; }
    setStatus(d.status || 'ok');
    setCameras(Array.isArray(d.cameras) ? d.cameras : []);
    setTimestamp(d.timestamp || null);
    setErrorMsg(d.status === 'error' ? (d.message || 'Discovery failed') : '');
    setPhase('ready');
  }, []);

  // Initial cache read on open / device change. Generation-guarded; cancels on unmount.
  useEffect(() => {
    const gen = ++genRef.current;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const draft = loadDiscoverDraft(deviceId);
    setPhase('loading'); setErrorMsg('');
    setSelected(draft.scope === draftScope ? (draft.selected || {}) : {}); // never mix add and per-target replace picks
    (async () => {
      const d = await backend.discoverCameras(deviceId, { token, signal: controller && controller.signal });
      if (gen !== genRef.current) return;
      applyResult(d);
    })();
    return () => { genRef.current += 1; if (controller) controller.abort(); };
  }, [deviceId, token, backend, applyResult, draftScope]);

  // A rescan starts outside the keyed fetch effect, so keep its controller separately and stop it when
  // the sheet closes. The generation token remains the second guard against a stale response landing.
  useEffect(() => () => {
    mountedRef.current = false;
    if (scanAbortRef.current) scanAbortRef.current.abort();
  }, []);

  // Persist selections per device so a browser refresh restores them (the scan itself re-fetches).
  useEffect(() => { saveDiscoverDraft(deviceId, { scope: draftScope, selected }); }, [deviceId, draftScope, selected]);

  // STALE-CACHE PROTECTION #1 — revalidate on every fresh list (initial load OR rescan). A persisted pick
  // whose camera VANISHED (off-network) or whose chosen stream URL is no longer offered (camera changed)
  // is auto-DESELECTED and surfaced, so we never silently stage a dead/stale rec_url. Runs only when a
  // real list is present (never on the empty/never-scanned state). Deps exclude `selected` so it fires on
  // list changes, not on the user's own toggles. (David 2026-08-27)
  useEffect(() => {
    if (phase !== 'ready' || !cameras.length) return;
    setSelected((sel) => {
      const keys = Object.keys(sel);
      if (!keys.length) return sel;
      const byIp = {};
      cameras.forEach((c) => { byIp[c.ip] = c; });
      const next = {};
      let dropped = 0;
      keys.forEach((ip) => {
        const cam = byIp[ip];
        const pick = sel[ip] && typeof sel[ip] === 'object' ? sel[ip] : null;
        const urls = cam ? streams(cam).map((s) => s.url) : [];
        const recStillThere = pick && (!pick.recUrl || urls.indexOf(pick.recUrl) !== -1);
        const liveStillThere = pick && (!pick.liveUrl || urls.indexOf(pick.liveUrl) !== -1);
        if (cam && recStillThere && liveStillThere) next[ip] = pick;
        else dropped += 1;
      });
      if (dropped) setStaleNote(`${dropped} selected camera${dropped > 1 ? 's are' : ' is'} no longer available (off-network or the stream changed) — removed from your selection.`);
      return dropped ? next : sel;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras, phase]);

  // Trigger a fresh scan, then poll the cache until its timestamp changes (or we time out).
  const runScan = useCallback(async () => {
    const gen = ++genRef.current;
    if (scanAbortRef.current) scanAbortRef.current.abort();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    scanAbortRef.current = controller;
    const requestOpts = { token, signal: controller && controller.signal };
    const priorTs = timestamp;
    setPhase('scanning'); setErrorMsg('');
    const r = await backend.rescanCameras(deviceId, requestOpts);
    if (gen !== genRef.current) return;
    if (!r.ok) {
      if (scanAbortRef.current === controller) scanAbortRef.current = null;
      setStatus(r.status === 'offline' ? 'offline' : 'error');
      setErrorMsg(r.status === 'offline' ? '' : (r.message || 'Could not start the scan'));
      setPhase('ready');
      return;
    }
    const deadline = Date.now() + SCAN_MAX_MS;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await sleep(POLL_MS);
      if (gen !== genRef.current) return;
      const d = await backend.discoverCameras(deviceId, requestOpts);
      if (gen !== genRef.current) return;
      if (d.ok && d.timestamp && d.timestamp !== priorTs) {
        if (scanAbortRef.current === controller) scanAbortRef.current = null;
        applyResult(d); return;
      } // fresh results landed
      if (Date.now() >= deadline) {
        if (scanAbortRef.current === controller) scanAbortRef.current = null;
        applyResult(d); return;
      } // gave up waiting; preserve the last error/offline state instead of stale cached rows
    }
  }, [backend, deviceId, token, timestamp, applyResult]);

  const toggle = (cam) => setSelected((sel) => {
    // Replace mode picks exactly ONE stream for the target camera; add mode multi-selects.
    if (replace) return sel[cam.ip] ? {} : { [cam.ip]: { name: dispName(cam), ...defaultUrls(cam) } };
    const next = { ...sel };
    if (next[cam.ip]) delete next[cam.ip];
    else next[cam.ip] = { name: dispName(cam), ...defaultUrls(cam) };
    return next;
  });
  // Expand/collapse a row to view its streams — purely a VIEW toggle, never touches selection. (David 2026-08-27)
  const toggleExpand = (cam) => setExpanded((ex) => ({ ...ex, [cam.ip]: !ex[cam.ip] }));
  const setName = (ip, name) => setSelected((sel) => (sel[ip] ? { ...sel, [ip]: { ...sel[ip], name } } : sel));
  const setRecUrl = (ip, recUrl) => setSelected((sel) => (sel[ip] ? { ...sel, [ip]: { ...sel[ip], recUrl } } : sel));
  const setLiveUrl = (ip, liveUrl) => setSelected((sel) => (sel[ip] ? { ...sel, [ip]: { ...sel[ip], liveUrl } } : sel));

  const selectedCount = Object.keys(selected).length;

  const commit = async () => {
    setBusyAdd(true);
    let skipped = 0;
    const out = Object.keys(selected).map((ip) => {
      const cam = cameras.find((c) => c.ip === ip);
      if (!cam) { skipped += 1; return null; }   // vanished since the last list — never stage a stale/gone camera
      const sel = selected[ip];
      const def = defaultUrls(cam);                        // rec = highest-res, live = lowest-res (the defaults)
      // The selection stores the chosen stream URLs directly (stable across a rescan reorder); fall back to
      // the defaults if a stored URL is somehow empty.
      const recUrl = sel.recUrl || def.recUrl;
      const liveUrl = sel.liveUrl || def.liveUrl || recUrl;
      return { name: (sel.name || dispName(cam)).trim(), recUrl, liveUrl, ip, make: cam.manufacturer, model: cam.model };
    }).filter((s) => s && s.recUrl);
    // STALE-CACHE PROTECTION #2 — visible-skip. If a pick vanished since the last list (rare; #1 usually
    // catches it first), surface it and DON'T close — the user re-checks rather than silently getting fewer.
    if (skipped) { setStaleNote(`${skipped} camera${skipped > 1 ? 's were' : ' was'} skipped — no longer in the latest results. Rescan and try again.`); setBusyAdd(false); return; }
    try {
      await onStage(out);
    } catch (e) {
      if (mountedRef.current) setStaleNote(`Could not stage the selected camera${out.length === 1 ? '' : 's'}. Try again.`);
    }
    if (mountedRef.current) setBusyAdd(false);
  };

  // Dismissing (Cancel / × / outside-click) with cameras selected but NOT yet added would silently drop
  // them — confirm first. Committing goes through onStage, never this, so "Add to batch" never prompts.
  // (David 2026-08-27)
  const requestClose = async () => {
    if (selectedCount > 0) {
      const discard = await askConfirm({
        title: 'Discard selected cameras?',
        message: `You've selected ${selectedCount} camera${selectedCount > 1 ? 's' : ''} but haven't added ${selectedCount > 1 ? 'them' : 'it'} to the batch. Discard your selection?`,
        confirmLabel: 'Discard', cancelLabel: 'Keep selecting', danger: true,
      });
      if (!discard) return;
    }
    onClose();
  };

  // Build the row list: classify each camera (add-able NEW, no-stream, staged, or already-assigned), then
  // order it into tiers (see below) so like kinds group together and the assigned band lands last.
  const rows = useMemo(() => {
    // TIER ORDER so each kind groups together (David 2026-08-27): NEW / add-able (0) at the top, then
    // no-stream / needs-credentials (1), then staged-this-batch (2), then the already-ASSIGNED cameras
    // (3) grouped at the very BOTTOM. Stable within each tier (scan order preserved).
    const tier = (r) => (r.selectable ? 0 : (r.assigned ? 3 : (r.added ? 2 : 1)));
    return cameras.map((cam) => {
      const host = hostOf(cam.ip);
      const assigned = assignedHosts.has(host);          // already assigned to the device (in the saved config)
      const added = !assigned && currentHosts.has(host);  // staged this batch, not yet pushed
      const usable = streams(cam).length > 0;
      return { cam, assigned, added, usable, selectable: !assigned && !added && usable };
    }).sort((a, b) => tier(a) - tier(b));
  }, [cameras, assignedHosts, currentHosts]);
  const newCount = rows.filter((r) => r.selectable).length;
  const allNewSelected = newCount > 0 && rows.every((r) => !r.selectable || selected[r.cam.ip]);
  const toggleSelectAll = () => {
    if (allNewSelected) { setSelected({}); return; }
    const next = {};
    rows.forEach((r) => { if (r.selectable) next[r.cam.ip] = { name: dispName(r.cam), ...defaultUrls(r.cam) }; });
    setSelected(next);
  };

  const scanBtnLabel = phase === 'scanning' ? 'Scanning…' : 'Rescan network';
  // STALE-CACHE PROTECTION #3 — the list is a cache; if it's over an hour old, nudge a rescan so picks are
  // less likely to be gone/changed by the time they're pushed. (Freshness cue only — zero extra latency.)
  const stale = !!(timestamp && Date.now() - Date.parse(timestamp) > 3600000);

  return (
    <Sheet className="app-sheet setup-discover" title={replace ? `Change stream — Camera ${replaceTarget}` : 'Discover cameras'} onClose={requestClose}>
      <div className="setup-discover__bar">
        <div className="setup-discover__meta">
          {phase === 'loading' ? 'Loading…'
            : status === 'offline' ? 'Offline'
            : status === 'error' ? 'Discovery unavailable'
            : timestamp ? (
              <>{cameras.length} found{newCount ? ` · ${newCount} new` : ''} · scanned {fmtAgo(timestamp)}
                {stale ? <span className="setup-discover__stale"> · may be stale — rescan</span> : null}</>
            )
            : 'Not scanned yet'}
        </div>
        <button type="button" className="setup-btn--secondary setup-discover__scan"
          disabled={phase === 'scanning' || phase === 'loading' || status === 'offline'}
          onClick={runScan}>{scanBtnLabel}</button>
      </div>

      {staleNote ? (
        <div className="setup-discover__stalenote" role="status">
          <span>{staleNote}</span>
          <button type="button" className="setup-discover__stalenote-x" aria-label="Dismiss" onClick={() => setStaleNote('')}>×</button>
        </div>
      ) : null}

      {phase === 'scanning' && cameras.length ? (
        <div className="setup-discover__scanbanner">
          <span className="setup-discover__scanbanner-spin" aria-hidden="true" />
          <span>Finding new cameras… this can take up to 2 minutes.</span>
        </div>
      ) : null}
      <div className="setup-discover__body">
        {phase === 'loading' ? (
          <div className="setup-discover__state"><div className="setup-discover__spinner" /></div>
        ) : (phase === 'scanning' && !cameras.length) ? (
          <div className="setup-discover__state">
            <div className="setup-discover__spinner" />
            <div className="setup-discover__state-title">Scanning the network…</div>
            <p className="setup-discover__state-sub">The device is probing its LAN for ONVIF cameras and testing credentials. This usually takes 30 seconds to 2 minutes.</p>
          </div>
        ) : status === 'offline' ? (
          <div className="setup-discover__state">
            <div className="setup-discover__state-title">Device is offline</div>
            <p className="setup-discover__state-sub">Discovery needs the device connected to the cloud. Reconnect it and try again.</p>
          </div>
        ) : status === 'error' ? (
          <div className="setup-discover__state">
            <div className="setup-discover__state-title">Couldn’t reach the device</div>
            <p className="setup-discover__state-sub">{errorMsg || 'The discovery request failed.'}</p>
          </div>
        ) : !cameras.length ? (
          <div className="setup-discover__state">
            <div className="setup-discover__state-title">No cameras found yet</div>
            <p className="setup-discover__state-sub">Run a network scan to find ONVIF cameras on this device’s network.</p>
          </div>
        ) : (() => {
          // Two INDEPENDENT columns (NOT a CSS grid): a grid shares row heights across columns, so
          // expanding one card grew the other column too. Splitting the rows into two side-by-side lists
          // means expanding a card pushes ONLY its own column; portrait stacks the halves back into one
          // continuous list. (David 2026-08-27)
          const renderRow = ({ cam, assigned, added, usable, selectable }) => {
              const st = bestStream(cam);
              const sel = selected[cam.ip];
              const on = !!sel;                       // CHECKED — this camera is in the batch
              const isOpen = !!expanded[cam.ip];      // EXPANDED — viewing its streams; independent of selection
              const opts = streams(cam);
              const eff = sel || { name: dispName(cam), ...defaultUrls(cam) };  // preview values before it's checked
              const info = (
                <div className="setup-discover__info">
                  <div className="setup-discover__name">
                    {dispName(cam)}
                    {assigned ? <span className="setup-pill setup-pill--modern">Assigned</span>
                      : added ? <span className="setup-pill setup-pill--staged">Added</span>
                      : !usable ? <span className="setup-pill setup-pill--legacy">No stream</span>
                      : <span className="setup-pill setup-pill--unknown">New</span>}
                  </div>
                  <div className="setup-discover__sub">
                    <span className="setup-discover__ip">{cam.ip}</span>
                    {usable ? <span>{streamLabel(st)}</span> : null}
                    {!usable ? <span className="setup-discover__warn">needs credentials</span> : null}
                  </div>
                </div>
              );
              return (
                <li key={cam.ip} className={'setup-discover__row' + (on ? ' setup-discover__row--sel' : '') + (!selectable ? ' setup-discover__row--off' : '')}>
                  {/* checkbox = SELECT (only); info button = EXPAND to view (only). Two separate targets. */}
                  <div className="setup-discover__cam">
                    {selectable ? (
                      <label className="setup-discover__check" title="Select to add to the batch">
                        <input type="checkbox" checked={on} onChange={() => toggle(cam)} />
                        <span className="setup-discover__check-box" aria-hidden="true" />
                      </label>
                    ) : (
                      <span className="setup-discover__check setup-discover__check--disabled" title={assigned ? 'Already assigned to this device' : added ? 'Added to this batch' : 'No stream available'}>
                        <span className="setup-discover__check-box" aria-hidden="true" />
                      </span>
                    )}
                    {/* SAME element (a div) for every row — selectable or not — so the checkbox aligns
                        identically. Only the selectable ones become an expand control (role/onClick/chevron).
                        (The old code used a <button> here and a bare div there — that divergence mis-shifted
                        the checkbox on every selectable row. David 2026-08-27.) */}
                    <div className="setup-discover__info-btn"
                      {...(selectable ? {
                        role: 'button', tabIndex: 0, 'aria-expanded': isOpen,
                        onClick: () => toggleExpand(cam),
                        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(cam); } },
                      } : {})}>
                      {info}
                      {selectable ? <span className={'setup-discover__chevron' + (isOpen ? ' setup-discover__chevron--open' : '')} aria-hidden="true">›</span> : null}
                    </div>
                  </div>
                  {selectable && isOpen ? (
                    <div className="setup-discover__edit">
                      <div className="setup-discover__fields">
                        <label className="setup-discover__field">
                          <span className="setup-discover__field-label">Camera name</span>
                          <input className={'setup-input' + (on ? '' : ' setup-input--readonly')} value={eff.name} disabled={!on}
                            onChange={(e) => setName(cam.ip, e.target.value)} placeholder={dispName(cam)} />
                        </label>
                        {opts.length > 1 ? (
                          <div className="setup-discover__field">
                            <span className="setup-discover__field-label">Recording stream</span>
                            <SheetSelect className="setup-input" value={String(Math.max(0, opts.findIndex((s) => s.url === eff.recUrl)))} disabled={!on}
                              title="Recording stream"
                              options={opts.map((s, i) => ({ value: String(i), label: streamLabel(s) || `Stream ${i + 1}` }))}
                              onChange={(v) => setRecUrl(cam.ip, (opts[Number(v)] || {}).url || '')} />
                          </div>
                        ) : null}
                        {opts.length > 1 ? (
                          <div className="setup-discover__field">
                            <span className="setup-discover__field-label">Live stream</span>
                            <SheetSelect className="setup-input" value={String(Math.max(0, opts.findIndex((s) => s.url === eff.liveUrl)))} disabled={!on}
                              title="Live stream"
                              options={opts.map((s, i) => ({ value: String(i), label: streamLabel(s) || `Stream ${i + 1}` }))}
                              onChange={(v) => setLiveUrl(cam.ip, (opts[Number(v)] || {}).url || '')} />
                          </div>
                        ) : null}
                        {!on ? <div className="setup-discover__edit-hint">Check the box to add this camera and edit its name + streams.</div> : null}
                      </div>
                    </div>
                  ) : null}
                </li>
              );
          };
          // rows are already sorted with the assigned / no-stream ones LAST (the useMemo sort above), so
          // split the two columns EVENLY at the midpoint: both columns are the same height and the
          // assigned group falls at the end — bottom of the right column. (David 2026-08-27)
          const half = Math.ceil(rows.length / 2);
          return (
            <div className={'setup-discover__cols' + (phase === 'scanning' ? ' setup-discover__list--dim' : '')}>
              <ul className="setup-discover__list">{rows.slice(0, half).map(renderRow)}</ul>
              <ul className="setup-discover__list">{rows.slice(half).map(renderRow)}</ul>
            </div>
          );
        })()}
      </div>

      {/* The select/add footer is only meaningful when the device is reachable — there's nothing to select
          or add when it's offline or the request failed, so hide the whole row (the X still closes the
          sheet). Logic lives in the shared component, so it applies at every viewport. (David 2026-08-27) */}
      {status !== 'error' && status !== 'offline' ? (
      <div className="setup-discover__actions">
        {/* Left: the count (wraps to ~2 lines on a narrow sheet) + Select-all beneath it. Right: the
            buttons, which keep their size. align-items:center keeps the left block centered against them. */}
        <div className="setup-discover__actions-left">
          <span className="setup-discover__count">{
            replace ? (selectedCount ? `Replaces Camera ${replaceTarget}` : `Pick the new stream for Camera ${replaceTarget}`)
              : (selectedCount ? <><b>{selectedCount}</b> selected</> : 'Select cameras to add')
          }</span>
          {!replace && newCount > 1 ? (
            <button type="button" className="setup-discover__selectall" disabled={phase === 'scanning'} onClick={toggleSelectAll}>
              {allNewSelected ? 'Clear' : 'Select all'}
            </button>
          ) : null}
        </div>
        <div className="setup-discover__btns">
          <button type="button" className="setup-btn--secondary" onClick={requestClose}>Cancel</button>
          <button type="button" className="setup-btn--save" disabled={!selectedCount || busyAdd || phase === 'scanning'} onClick={commit}>
            {replace ? 'Replace stream' : (selectedCount ? `Add ${selectedCount} to batch` : 'Add to batch')}
          </button>
        </div>
      </div>
      ) : null}
    </Sheet>
  );
};

// "scanned 4 min ago" from an ISO timestamp; tolerant of a missing/odd value.
function fmtAgo(ts) {
  const t = Date.parse(ts);
  if (!t) return 'recently';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.round(h / 24)} d ago`;
}

export default DiscoverCamerasSheet;
